import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import RoleService from "../../services/roleService";
import { userService } from "../../services/userService";
import { Helmet } from "react-helmet";

import { useAuth } from "../../hooks/useAuth";
import { useRealtimeSync } from "../../hooks/useRealtimeSync";
import Header from "../../components/ui/Header";
import SearchBar from "./components/SearchBar";
import RoleListItem from "./components/RoleListItem";
import FilterStats from "./components/FilterStats";
import LoadingSkeleton from "./components/LoadingSkeleton";
import EmptyState from "./components/EmptyState";
import AddCharacterModal from "./components/AddCharacterModal";
import EditRoleTypesModal from "./components/EditRoleTypesModal";
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';



export default function AllRoles() {
  const navigate = useNavigate();
  const { user, isSuperUser } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showAddCharacterModal, setShowAddCharacterModal] = useState(false);
  const [showEditRoleTypesModal, setShowEditRoleTypesModal] = useState(false);
  const [viewMode, setViewMode] = useState('compact'); // 'full' or 'compact' - Default to compact
  const [filterNoSongs, setFilterNoSongs] = useState(false);
  const [filterNoVocalRange, setFilterNoVocalRange] = useState(false);

  // Featured roles (superuser only)
  const [featuredRoleIds, setFeaturedRoleIds] = useState(new Set());

  // Favorite roles (all logged-in users)
  const [favoriteRoleIds, setFavoriteRoleIds] = useState(new Set());

  // ✅ Real-time sync for roles table
  useRealtimeSync('roles', {
    onInsert: (newRole) => {
      console.log('New role added:', newRole);
      // Refetch roles to get complete data with relationships
      fetchRoles();
    },
    onUpdate: (updatedRole) => {
      console.log('Role updated:', updatedRole);
      // Update the role in the list
      setRoles(prevRoles => 
        prevRoles?.map(role => 
          role?.id === updatedRole?.id ? { ...role, ...updatedRole } : role
        )
      );
    },
    onDelete: (deletedRole) => {
      console.log('Role deleted:', deletedRole);
      // Remove the role from the list
      setRoles(prevRoles => 
        prevRoles?.filter(role => role?.id !== deletedRole?.id)
      );
    }
  });

  // Load favorites when user is available
  useEffect(() => {
    if (!user?.id) return;
    const loadFavorites = async () => {
      try {
        const { favorites, error: favError } = await userService?.getFavoriteRoles(user?.id);
        if (!favError && favorites) {
          setFavoriteRoleIds(new Set(favorites?.map(r => r?.id)));
        }
      } catch (err) {
        console.error('Error loading favorite roles:', err);
      }
    };
    loadFavorites();
  }, [user?.id]);

  // Fetch roles from database
  const fetchRoles = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await RoleService?.getRolesWithSongCounts();

      if (fetchError) {
        setError('Failed to load roles. Please try again.');
        setRoles([]);
      } else {
        const rolesData = data || [];
        setRoles(rolesData);
        setFeaturedRoleIds(new Set(rolesData?.filter(r => r?.is_featured)?.map(r => r?.id)));
        setHasSearched(true);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please refresh the page.');
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  // Check if user has admin/editor permissions
  const isAdminOrEditor = useMemo(() => {
    return user?.role === 'superuser' || user?.role === 'editor';
  }, [user?.role]);

  useEffect(() => {
    // Load roles on component mount
    const initializeData = async () => {
      await fetchRoles();
    };
    
    initializeData();
  }, []);

  const loadRoles = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await RoleService?.getRolesWithSongCounts();

      if (fetchError) {
        setError('Failed to load roles. Please try again.');
        setRoles([]);
      } else {
        const rolesData = data || [];
        setRoles(rolesData);
        setFeaturedRoleIds(new Set(rolesData?.filter(r => r?.is_featured)?.map(r => r?.id)));
        setHasSearched(true);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please refresh the page.');
      setRoles([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter roles based on search term and new filters
  const filteredRoles = useMemo(() => {
    if (!hasSearched) {
      return [];
    }

    let filtered = roles;
    
    // Apply fuzzy search filter
    if (activeSearchTerm?.trim()) {
      const searchLower = activeSearchTerm?.toLowerCase()?.trim();
      const searchWords = searchLower?.split(/\s+/);
      
      filtered = roles?.filter(role => {
        const searchableText = [
          role?.character_name,
          role?.description,
          role?.vocal_range,
          role?.voice_type,
          role?.role_type,
          role?.musicals?.title,
          role?.musicals?.composer,
          role?.musicals?.lyricist
        ]?.filter(Boolean)?.join(' ')?.toLowerCase();
        
        // Fuzzy match: Check if all search words appear in searchable text
        return searchWords?.every(word => searchableText?.includes(word));
      });
    }
    
    // Apply no songs filter
    if (filterNoSongs) {
      filtered = filtered?.filter(role => 
        !role?.song_count || role?.song_count === 0
      );
    }

    // Apply no vocal range filter
    if (filterNoVocalRange) {
      filtered = filtered?.filter(role => 
        !role?.vocal_range || role?.vocal_range?.trim() === '' || role?.vocal_range === 'Not specified'
      );
    }

    return filtered;
  }, [roles, activeSearchTerm, hasSearched, filterNoSongs, filterNoVocalRange]);

  const handleSearchInputChange = (value) => {
    setSearchTerm(value);
  };

  const handleSearchSubmit = async (value) => {
    setActiveSearchTerm(value);
    setHasSearched(true);

    if (value?.trim()) {
      setIsLoading(true);
      try {
        const { data, error: searchError } = await RoleService?.searchRolesFuzzy(value);
        if (searchError) {
          console.error('Error searching roles:', searchError);
          // Show user-friendly error but still attempt to show results
          setError('Search may be incomplete. Please try a different term.');
          setRoles(data || []);
        } else {
          setRoles(data || []);
          setError(null);
        }
      } catch (err) {
        console.error('Error in role search:', err);
        setError('Search failed. Please try again.');
        setRoles([]);
      } finally {
        setIsLoading(false);
      }
    } else {
      loadRoles();
    }
  };

  const handleShowAllRoles = () => {
    setSearchTerm('');
    setActiveSearchTerm('');
    loadRoles();
  };

  const handleRoleClick = (role) => {
    // Navigate to role detail page with role ID
    navigate(`/role-detail-page?id=${role?.id}`);
  };

  const handleToggleViewMode = () => {
    setViewMode(prevMode => prevMode === 'full' ? 'compact' : 'full');
  };

  const handleToggleNoSongsFilter = () => {
    setFilterNoSongs(!filterNoSongs);
  };

  const handleToggleNoVocalRangeFilter = () => {
    setFilterNoVocalRange(!filterNoVocalRange);
  };

  const handleRefresh = () => {
    setSearchTerm('');
    setActiveSearchTerm('');
    setHasSearched(false);
    setRoles([]);
    setViewMode('compact');
    setFilterNoSongs(false);
    setFilterNoVocalRange(false);
  };

  const handleAddCharacterSuccess = (newRole) => {
    // Refresh the roles list after successful creation
    if (hasSearched) {
      if (activeSearchTerm) {
        handleSearchSubmit(activeSearchTerm);
      } else {
        loadRoles();
      }
    }
    setShowAddCharacterModal(false);
  };

  const handleEditRoleTypesClick = () => {
    if (!isAdminOrEditor) {
      // Show user-friendly message for non-admin users
      setError('Admin privileges required to manage role types. Contact your administrator for access.');
      setTimeout(() => setError(null), 5000);
      return;
    }
    setShowEditRoleTypesModal(true);
  };

  const handleEditRoleTypesSuccess = () => {
    setShowEditRoleTypesModal(false);
  };

  const handleFeatureToggle = async (role) => {
    if (!isSuperUser?.()) return;
    const roleId = role?.id;
    const isCurrentlyFeatured = featuredRoleIds?.has(roleId);
    // Optimistic update
    setFeaturedRoleIds(prev => {
      const next = new Set(prev);
      if (isCurrentlyFeatured) next?.delete(roleId);
      else next?.add(roleId);
      return next;
    });
    try {
      await RoleService?.updateRole(roleId, { is_featured: !isCurrentlyFeatured }, user?.id);
    } catch (err) {
      // Revert on error
      setFeaturedRoleIds(prev => {
        const next = new Set(prev);
        if (isCurrentlyFeatured) next?.add(roleId);
        else next?.delete(roleId);
        return next;
      });
    }
  };

  const handleFavoriteToggle = async (role) => {
    if (!user?.id) return;
    const roleId = role?.id;
    const isCurrentlyFavorited = favoriteRoleIds?.has(roleId);
    // Optimistic update
    setFavoriteRoleIds(prev => {
      const next = new Set(prev);
      if (isCurrentlyFavorited) next?.delete(roleId);
      else next?.add(roleId);
      return next;
    });
    try {
      if (isCurrentlyFavorited) {
        await userService?.removeFavoriteRole(user?.id, roleId);
      } else {
        await userService?.addFavoriteRole(user?.id, roleId);
      }
    } catch (err) {
      // Revert on error
      setFavoriteRoleIds(prev => {
        const next = new Set(prev);
        if (isCurrentlyFavorited) next?.add(roleId);
        else next?.delete(roleId);
        return next;
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-6xl">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="h-6 bg-white bg-opacity-20 rounded shimmer w-48"></div>
              <div className="h-4 bg-white bg-opacity-20 rounded shimmer w-32"></div>
            </div>
            <div className="h-10 bg-white bg-opacity-20 rounded shimmer"></div>
          </div>
          <LoadingSkeleton />
        </main>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>All Roles - RoleCall</title>
        <meta name="description" content="Browse and search musical theater character roles with vocal ranges, song counts, and detailed casting information." />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900">
        <Header />
        <main className="container mx-auto px-6 py-8 max-w-6xl">
          <div className="mb-8">
            <h1 className="text-3xl font-heading font-bold text-white mb-2">
              All Roles
            </h1>
            <p className="text-blue-100 font-body">
              {hasSearched 
                ? "Browse musical theater character roles with comprehensive casting details" :"Search for character roles or view all roles in the database"
              }
            </p>
          </div>

          {/* Search Bar */}
          <SearchBar
            searchTerm={searchTerm}
            onSearchChange={handleSearchInputChange}
            onSearchSubmit={handleSearchSubmit}
            totalRoles={hasSearched ? roles?.length : 0}
          />

          {/* Filter Stats */}
          {hasSearched && (
            <FilterStats
              totalRoles={roles?.length}
              filteredRoles={filteredRoles?.length}
              searchTerm={activeSearchTerm}
            />
          )}

          {/* Controls */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex items-center space-x-4 flex-wrap">
              <Button
                variant="outline"
                onClick={handleRefresh}
                iconName="RefreshCw"
                iconPosition="left"
                disabled={isLoading}
                className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
              >
                Refresh
              </Button>

              {/* View Mode Toggle - only show when roles are displayed */}
              {hasSearched && (
                <Button
                  variant="outline"
                  onClick={handleToggleViewMode}
                  iconName={viewMode === 'full' ? 'List' : 'Grid'}
                  iconPosition="left"
                  className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
                >
                  {viewMode === 'full' ? 'Compact View' : 'Full View'}
                </Button>
              )}

              {/* Filter Buttons - only show when roles are displayed */}
              {hasSearched && (
                <>
                  <Button
                    variant="outline"
                    onClick={handleToggleNoSongsFilter}
                    iconName="MusicOff"
                    iconPosition="left"
                    className={`rounded-lg transition-all ${
                      filterNoSongs
                        ? 'bg-orange-500 bg-opacity-30 border-orange-300 text-white' :'border-white text-white hover:bg-white hover:text-indigo-900'
                    }`}
                  >
                    No Songs
                  </Button>

                  <Button
                    variant="outline"
                    onClick={handleToggleNoVocalRangeFilter}
                    iconName="Volume2"
                    iconPosition="left"
                    className={`rounded-lg transition-all ${
                      filterNoVocalRange
                        ? 'bg-pink-500 bg-opacity-30 border-pink-300 text-white' :'border-white text-white hover:bg-white hover:text-indigo-900'
                    }`}
                  >
                    No Vocal Range
                  </Button>
                </>
              )}
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center space-x-4">
              {isAdminOrEditor && (
                <Button
                  variant="outline"
                  onClick={handleEditRoleTypesClick}
                  iconName="Settings"
                  iconPosition="left"
                  className="border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-white rounded-lg shadow-lg transition-all duration-200"
                >
                  Edit Role Types
                </Button>
              )}
              <Button
                variant="primary"
                onClick={() => setShowAddCharacterModal(true)}
                iconName="UserPlus"
                iconPosition="left"
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white border-0 rounded-lg shadow-lg transition-all duration-200"
              >
                Add Character
              </Button>
            </div>
          </div>

          {/* Add Character Modal */}
          <AddCharacterModal
            isOpen={showAddCharacterModal}
            onClose={() => setShowAddCharacterModal(false)}
            onSuccess={handleAddCharacterSuccess}
          />

          {/* Edit Role Types Modal */}
          <EditRoleTypesModal
            isOpen={showEditRoleTypesModal}
            onClose={() => setShowEditRoleTypesModal(false)}
            onSuccess={handleEditRoleTypesSuccess}
          />

          {/* Error State */}
          {error && (
            <div className="bg-red-50 bg-opacity-90 border border-red-200 rounded-xl p-6 mb-6">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={20} className="text-red-800" />
                <p className="text-red-800 font-medium">{error}</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                className="mt-4 border-red-400 text-red-600 hover:bg-red-400 hover:text-white rounded-lg"
                iconName="RefreshCw"
                iconPosition="left"
              >
                Try Again
              </Button>
            </div>
          )}

          {/* Role List - conditional rendering based on view mode */}
          {!hasSearched ? (
            // Initial state - no search performed yet
            (<div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-12 text-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="Search" size={32} className="text-blue-100" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-white mb-3">
                Search for Character Roles
              </h3>
              <p className="text-blue-100 font-body mb-8 max-w-md mx-auto leading-relaxed">
                Use the search bar above to find specific character roles, or click "Show All Roles" to view the complete character database.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm('');
                    setActiveSearchTerm('');
                  }}
                  iconName="X"
                  iconPosition="left"
                  className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
                >
                  Clear Search
                </Button>
                
                <Button
                  variant="outline"
                  onClick={handleShowAllRoles}
                  iconName="Users"
                  iconPosition="left"
                  disabled={isLoading}
                  className="border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-white rounded-lg"
                >
                  Show All Roles
                </Button>
              </div>
            </div>)
          ) : filteredRoles?.length === 0 ? (
            // Search performed but no results
            (activeSearchTerm ? (<div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl p-12 text-center">
              <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="Search" size={32} className="text-blue-100" />
              </div>
              <h3 className="text-xl font-heading font-semibold text-white mb-3">
                No Roles Found
              </h3>
              <p className="text-blue-100 font-body mb-8 max-w-md mx-auto leading-relaxed">
                No character roles match your search criteria. Try adjusting your search terms or browse all available roles.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm('');
                    setActiveSearchTerm('');
                  }}
                  iconName="X"
                  iconPosition="left"
                  className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
                >
                  Clear Search
                </Button>
                
                <Button
                  variant="outline"
                  onClick={handleShowAllRoles}
                  iconName="Users"
                  iconPosition="left"
                  disabled={isLoading}
                  className="border-purple-400 text-purple-400 hover:bg-purple-400 hover:text-white rounded-lg"
                >
                  Show All Roles
                </Button>
              </div>
            </div>) : (<EmptyState />))
          ) : (
            // Show results
            (<div className={viewMode === 'full' ? 'space-y-4' : 'space-y-2'}>
              {filteredRoles?.map((role) => (
                <RoleListItem
                  key={role?.id}
                  role={role}
                  onClick={handleRoleClick}
                  viewMode={viewMode}
                  isFeatured={featuredRoleIds?.has(role?.id)}
                  onFeatureToggle={handleFeatureToggle}
                  isFavorited={favoriteRoleIds?.has(role?.id)}
                  onFavoriteToggle={user?.id ? handleFavoriteToggle : null}
                  isSuperUser={isSuperUser}
                />
              ))}
            </div>)
          )}

          {/* Footer Stats */}
          {hasSearched && filteredRoles?.length > 0 && (
            <div className="mt-8 pt-6 border-t border-white border-opacity-20">
              <div className="flex items-center justify-between text-sm text-blue-100">
                <div className="flex items-center space-x-2">
                  <Icon name="Users" size={16} />
                  <span className="font-caption">
                    Showing {filteredRoles?.length} of {roles?.length} character roles
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Calendar" size={16} />
                  <span className="font-caption">
                    Last updated: {new Date()?.toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </span>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
}